import { useState } from 'react';
import { Heading1, Paragraph, Small } from '@/components/Typography';
import { ImageWithFallback } from '@/components/ImageWithFallback';
import { IconButton } from '@/components/IconButton';
import type { GeneratedStorySpreadWithImageUrl } from 'schemas';
import { storyPreviewMessages } from './storyPreview.messages';

interface StoryPreviewProps {
  title: string;
  spreads: GeneratedStorySpreadWithImageUrl[];
  showFinalEntryCta?: boolean;
}

export function StoryPreview({
  title,
  spreads,
  showFinalEntryCta = false,
}: StoryPreviewProps) {
  // Sort spreads by spread number from lowest to highest
  const sortedSpreads = [...spreads].sort((a, b) => a.spreadNum - b.spreadNum);

  // Add final entry if showFinalEntryCta is true
  const hasFinalEntry = showFinalEntryCta;
  const totalSpreads = sortedSpreads.length;
  const totalEntries = hasFinalEntry ? totalSpreads + 1 : totalSpreads;
  const finalEntryIndex = totalSpreads;

  const [currentSpreadIndex, setCurrentSpreadIndex] = useState(0);
  const isOnFinalEntry =
    hasFinalEntry && currentSpreadIndex === finalEntryIndex;
  const currentSpread = isOnFinalEntry
    ? null
    : sortedSpreads[currentSpreadIndex];

  const goToPreviousSpread = () => {
    setCurrentSpreadIndex((prev) => (prev > 0 ? prev - 1 : prev));
  };

  const goToNextSpread = () => {
    setCurrentSpreadIndex((prev) =>
      prev < totalEntries - 1 ? prev + 1 : prev
    );
  };

  if (!sortedSpreads.length) {
    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center p-8">
        <Paragraph>{storyPreviewMessages.noSpreadAvailable}</Paragraph>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col items-center justify-center space-y-6 lg:p-6">
      {/* Story Title */}
      <Heading1 className="max-w-2xl text-center">{title}</Heading1>

      {/* Story Spread Card */}
      <div className="w-full max-w-4xl flex-1">
        {isOnFinalEntry ? (
          // Final entry CTA
          <div className="mx-auto mb-6 flex aspect-square w-full max-w-md items-center justify-center">
            <div className="space-y-4 text-center">
              <div
                className="text-6xl"
                aria-label={storyPreviewMessages.celebration}
              >
                🎉
              </div>
              <Heading1 className="text-2xl">
                {storyPreviewMessages.checkoutTitle}
              </Heading1>
              <Paragraph className="text-lg">
                {storyPreviewMessages.checkoutMessage}
              </Paragraph>
            </div>
          </div>
        ) : currentSpread ? (
          // Regular spread content
          <>
            {/* Image/Content Area */}
            <div className="mx-auto mb-6 aspect-square w-full max-w-md">
              <ImageWithFallback
                src={currentSpread.imageUrl || undefined}
                alt={currentSpread.imagePrompt}
              />
            </div>

            {/* Spread Content */}
            <div className="mb-6 flex-1 overflow-y-auto">
              <Paragraph>{currentSpread.content}</Paragraph>
            </div>
          </>
        ) : (
          // Fallback for invalid state
          <div className="flex min-h-[400px] flex-col items-center justify-center p-8">
            <Paragraph>{storyPreviewMessages.noSpreadAvailable}</Paragraph>
          </div>
        )}
      </div>

      {/* Navigation Arrows */}
      <div className="flex w-full items-center justify-between">
        <IconButton
          icon="ChevronLeft"
          sizeVariant="large"
          onClick={goToPreviousSpread}
          disabled={currentSpreadIndex === 0}
        />
        <Small>
          {storyPreviewMessages.spreadIndicator(
            currentSpreadIndex + 1,
            totalEntries
          )}
        </Small>
        <IconButton
          icon="ChevronRight"
          sizeVariant="large"
          onClick={goToNextSpread}
          disabled={currentSpreadIndex === totalEntries - 1}
        />
      </div>
    </div>
  );
}
